/* Expected Delivery estimate — refresh PDP.
   All date math is client-side, keyed to the warehouse timezone; config is
   injected by snippets/expected-delivery.liquid via data-* attributes.
   Any failure leaves the server-rendered fallback line in place. */
if (!customElements.get('expected-delivery')) {
  customElements.define(
    'expected-delivery',
    class extends HTMLElement {
      connectedCallback() {
        this.textEl = this.querySelector('[data-delivery-text]');
        this.cutoffEl = this.querySelector('[data-delivery-cutoff]');
        if (!this.textEl) return;

        var holidays = [];
        try {
          holidays = JSON.parse(this.dataset.holidays || '[]') || [];
        } catch (_) {}

        this.cfg = {
          transitDays: parseInt(this.dataset.transitDays, 10) || 5,
          cutoff: this.dataset.cutoff || '',
          tz: this.dataset.cutoffTz || 'America/Chicago',
          satDelivery: this.dataset.saturdayDelivery === 'true',
          holidays: holidays,
          showCutoff: this.dataset.showCutoff === 'true',
          // Only render the countdown when it's imminent enough to be
          // actionable — an all-day "14h left" reads as noise.
          cutoffShowHours: parseFloat(this.dataset.cutoffShowHours) || 6,
          prefix: this.dataset.prefix || 'Get it by',
          subPrefix: this.dataset.subPrefix || 'Your first box arrives by',
          backorderText: this.dataset.backorderText || ''
        };
        this.state = {
          available: this.dataset.available === 'true',
          backorder: this.dataset.backorder === 'true',
          isSubscription: false,
          planName: ''
        };

        // Subscription copy: the Skio picker bubbles this on hydration and on
        // every plan/variant change (skio-plan-picker-component.js). The store
        // can default to Autoship, so the very first render may be the
        // subscription line — render() must be idempotent.
        this._onPlanChange = (e) => {
          if (!e.detail) return;
          this.state.isSubscription = !!e.detail.isSubscription;
          this.state.planName = e.detail.planName || '';
          this.render();
        };
        document.addEventListener('pdp:plan-change', this._onPlanChange);

        // Variant switches: availability comes from the option's data attrs
        // (the Liquid-rendered state only reflects the initial variant).
        var select = document.getElementById(this.dataset.variantSelect || '');
        if (select) {
          this._onVariantChange = (e) => {
            var opt = e.target.selectedOptions && e.target.selectedOptions[0];
            if (!opt) return;
            this.state.available = opt.dataset.available === 'true';
            this.state.backorder = !this.state.available && opt.dataset.inventoryPolicy === 'continue';
            this.render();
          };
          select.addEventListener('change', this._onVariantChange);
        }

        try {
          this.render();
        } catch (_) {
          /* leave the server-rendered fallback line */
        }
        // Minute granularity keeps the countdown calm (no seconds) and rolls
        // the date forward naturally when the cutoff passes.
        this.timer = setInterval(() => {
          try {
            this.render();
          } catch (_) {}
        }, 60000);

        this.emitAnalytics();
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        document.removeEventListener('pdp:plan-change', this._onPlanChange);
      }

      /* Warehouse "now" via Intl — DST-safe, no epoch offset math. */
      tzNow() {
        var parts = new Intl.DateTimeFormat('en-US', {
          timeZone: this.cfg.tz,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23'
        })
          .formatToParts(new Date())
          .reduce(function (acc, p) {
            acc[p.type] = p.value;
            return acc;
          }, {});
        return { y: +parts.year, m: +parts.month, d: +parts.day, hh: +parts.hour, mm: +parts.minute };
      }

      isHoliday(dt) {
        return this.cfg.holidays.indexOf(dt.toISOString().slice(0, 10)) !== -1;
      }

      /* Ship days: Mon–Fri minus fulfillment holidays. */
      isWorkday(dt) {
        var dow = dt.getUTCDay();
        if (dow === 0 || dow === 6) return false;
        return !this.isHoliday(dt);
      }

      /* Delivery days: no Sunday; Saturday only if enabled; same holiday list
         (carriers observe roughly the same federal holidays — errs conservative). */
      isDeliveryDay(dt) {
        var dow = dt.getUTCDay();
        if (dow === 0) return false;
        if (dow === 6 && !this.cfg.satDelivery) return false;
        return !this.isHoliday(dt);
      }

      compute() {
        var now = this.tzNow();
        var cutoffMinsLeft = null;
        if (this.cfg.cutoff) {
          var hm = this.cfg.cutoff.split(':');
          // Pure wall-clock diff in the warehouse TZ — no epoch conversion.
          cutoffMinsLeft = +hm[0] * 60 + (+hm[1] || 0) - (now.hh * 60 + now.mm);
        }

        var ship = new Date(Date.UTC(now.y, now.m - 1, now.d));
        // No configured cutoff => never promise same-day handoff.
        var shipsToday = this.cfg.cutoff ? cutoffMinsLeft > 0 && this.isWorkday(ship) : false;
        if (!shipsToday) {
          do {
            ship.setUTCDate(ship.getUTCDate() + 1);
          } while (!this.isWorkday(ship));
        }

        var eta = new Date(ship.getTime());
        for (var i = 0; i < this.cfg.transitDays; i++) {
          do {
            eta.setUTCDate(eta.getUTCDate() + 1);
          } while (!this.isDeliveryDay(eta));
        }
        return { eta: eta, shipsToday: shipsToday, cutoffMinsLeft: cutoffMinsLeft };
      }

      formatDate(dt, opts) {
        return new Intl.DateTimeFormat(
          'en-US',
          Object.assign({ month: 'short', day: 'numeric', timeZone: 'UTC' }, opts)
        ).format(dt);
      }

      /* Skio plan names read like "Every 1 Month" / "Every 3 Months".
         Normalize for prose: "1 Month" -> "month", "3 Months" -> "3 months". */
      planInterval() {
        var m = /every\s+(\d+)\s+(\w+)/i.exec(this.state.planName);
        if (m) {
          var n = parseInt(m[1], 10);
          var unit = m[2].toLowerCase().replace(/s$/, '');
          return n === 1 ? unit : n + ' ' + unit + 's';
        }
        m = /every\s+(.+)$/i.exec(this.state.planName);
        return m ? m[1].trim().toLowerCase() : '';
      }

      setLine(prefixText, emText, suffixText) {
        var frag = document.createDocumentFragment();
        frag.appendChild(document.createTextNode(prefixText + ' '));
        var em = document.createElement('span');
        em.className = 'pdp-delivery__em';
        em.textContent = emText;
        frag.appendChild(em);
        if (suffixText) frag.appendChild(document.createTextNode(suffixText));
        this.textEl.replaceChildren(frag);
      }

      render() {
        if (!this.state.available && !this.state.backorder) {
          this.hidden = true;
          return;
        }
        this.hidden = false;

        if (this.state.backorder) {
          this.textEl.textContent = this.cfg.backorderText;
          this.cutoffEl.hidden = true;
          return;
        }

        var r = this.compute();
        var longDate = this.formatDate(r.eta, { weekday: 'short' });

        if (this.state.isSubscription) {
          var interval = this.planInterval();
          this.setLine(this.cfg.subPrefix, longDate, interval ? ', then every ' + interval : '');
        } else {
          this.setLine(this.cfg.prefix, longDate, '');
        }

        var showCutoff = this.cfg.showCutoff && r.shipsToday && r.cutoffMinsLeft > 0
          && r.cutoffMinsLeft <= this.cfg.cutoffShowHours * 60;
        if (showCutoff) {
          var h = Math.floor(r.cutoffMinsLeft / 60);
          var m = r.cutoffMinsLeft % 60;
          var left = h > 0 ? h + 'h ' + m + 'm' : m + 'm';
          // Benefit first, deadline second; the promise date sits directly
          // above, so it isn't repeated here.
          this.cutoffEl.textContent = 'Order within ' + left + ' for fastest delivery';
        }
        this.cutoffEl.hidden = !showCutoff;
      }

      emitAnalytics() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'pdp_delivery_estimate_view',
          delivery_transit_source: this.dataset.transitSource,
          delivery_has_countdown: !!this.cfg.cutoff
        });
      }
    }
  );
}
